import { useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  educationKeys,
  EDUCATION_REGISTRATION_STATUS_LABEL,
} from '@platform/billing';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { useMyEducationRegistration } from '../api/use-education.query';
import { useResumeEducationPayment } from '../hooks/useEducationCheckout';
import { EducationProgressSteps } from '../components/EducationProgressSteps';
import { EDUCATION_STEP } from '../constants/education-step.constant';
import {
  formatEducationPrice,
  formatSessionDateTime,
} from '../lib/education-format';

/** 접수 상세 — 회차별 인원명부와 온라인 접속 링크. */
export function MyEducationDetailView() {
  const { id } = useParams<{ id: string }>();
  const { data: registration, isLoading, isError } = useMyEducationRegistration(id);

  const qc = useQueryClient();
  // 재개 흐름이 결제창 없이 끝나는 경우(이미 승인된 결제였다) 상세를 다시 읽어야
  // 확정된 상태가 화면에 반영된다.
  const refetch = useCallback(() => {
    void qc.invalidateQueries({ queryKey: educationKeys.all });
  }, [qc]);
  const { resume, resuming } = useResumeEducationPayment(refetch);

  if (isLoading)
    return <p className="py-24 text-center text-sm text-gray-500">불러오는 중…</p>;
  if (isError || !registration)
    return (
      <p className="py-24 text-center text-sm text-red-600">
        접수 내역을 불러오지 못했습니다.
      </p>
    );

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <Link
        to={PATHS.EDUCATION_REGISTRATIONS}
        className="text-sm text-gray-500 hover:text-gray-800 dark:text-neutral-400"
      >
        ← 접수 내역
      </Link>

      <div className="mt-5">
        <EducationProgressSteps activeStep={EDUCATION_STEP.DONE} />
      </div>

      <header className="mt-6">
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-white/10 dark:text-neutral-200">
            {EDUCATION_REGISTRATION_STATUS_LABEL[registration.status]}
          </span>
          <span className="text-sm text-gray-400">
            {registration.registrationNo}
          </span>
        </div>
        <h1 className="mt-2 text-xl font-bold text-gray-900 dark:text-neutral-100">
          결제 금액 {formatEducationPrice(registration.totalAmount)}
        </h1>
      </header>

      {registration.status === 'PENDING' && (
        <div className="mt-4 rounded-lg bg-amber-50 px-4 py-3 dark:bg-amber-500/10">
          <p className="text-sm text-amber-800 dark:text-amber-300">
            결제가 완료되지 않았습니다. 결제를 마치지 않으면 확보된 자리가 자동으로
            해제됩니다.
          </p>
          {registration.resumable && (
            <button
              type="button"
              disabled={resuming}
              onClick={() => void resume(registration.id)}
              className="mt-2 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:bg-gray-300"
            >
              {resuming ? '결제창 여는 중…' : '결제 이어서 하기'}
            </button>
          )}
        </div>
      )}
      {registration.status === 'EXPIRED' && (
        <p className="mt-4 rounded-lg bg-gray-100 px-4 py-3 text-sm text-gray-600 dark:bg-white/10 dark:text-neutral-300">
          결제 기한이 지나 자리가 해제되었습니다. 회차에 자리가 남아 있다면 다시
          접수해 주세요.
        </p>
      )}
      {registration.status === 'CANCELED' && registration.cancelReason && (
        <p className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">
          취소 사유: {registration.cancelReason}
        </p>
      )}

      {registration.items.map((item) => (
        <section
          key={item.id}
          className="mt-6 rounded-xl border border-gray-200 p-4 dark:border-white/15"
        >
          <h2 className="font-semibold text-gray-900 dark:text-neutral-100">
            {item.sessionTitle}
          </h2>
          <p className="mt-0.5 text-xs text-gray-400 dark:text-neutral-500">
            {item.courseTitle} · {formatSessionDateTime(item.sessionStartAt)} ·{' '}
            {formatEducationPrice(item.unitPrice)} × {item.quantity}명
          </p>

          {item.onlineJoinUrl && (
            <a
              href={item.onlineJoinUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-block rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
            >
              온라인 교육 입장
            </a>
          )}

          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[28rem] text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs text-gray-400 dark:border-white/10">
                  <th className="py-2 font-medium">소속회사</th>
                  <th className="py-2 font-medium">이름</th>
                  <th className="py-2 font-medium">연락처</th>
                  <th className="py-2 font-medium">이메일</th>
                </tr>
              </thead>
              <tbody>
                {item.attendees.map((a) => (
                  <tr
                    key={a.id}
                    className="border-b border-gray-50 last:border-0 dark:border-white/5"
                  >
                    <td className="py-2 text-gray-500 dark:text-neutral-400">
                      {a.companyName ?? '-'}
                    </td>
                    <td className="py-2 text-gray-900 dark:text-neutral-100">
                      {a.name}
                    </td>
                    <td className="py-2 text-gray-500 dark:text-neutral-400">
                      {a.phone}
                    </td>
                    <td className="py-2 text-gray-500 dark:text-neutral-400">
                      {a.email ?? '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      <p className="mt-6 text-xs text-gray-500 dark:text-neutral-400">
        접수 취소·환불은 고객센터로 문의해 주세요. 교육 시작 2일 전까지 전액, 1일
        전 50%, 당일 이후에는 환불되지 않습니다.
      </p>
    </div>
  );
}
